import Request from '@/apis/config';
import { QUERY_KEYS } from '@/interfaces/constants';
import { useQuery } from '@tanstack/react-query';

const URL_ANNOUNCEMENT_ADMIN_LIST = '/noti-service/announcement/admin';
const URL_ANNOUNCEMENT_DASHBOARD_LIST = '/noti-service/announcement/dashboard';

/** Same shape as `AnnouncementExistingMedia` in the form (kept here to avoid apis → pages import). */
export type AnnouncementAdminDetailMedia = {
  type: string;
  url: string;
  telegram_file_id?: string;
};

export type AnnouncementAdminDetailFormShape = {
  id?: string | number;
  title?: string;
  vendor?: string;
  purpose?: string;
  period?: string;
  content?: string;
  media: AnnouncementAdminDetailMedia[];
};

function asRecord(v: unknown): Record<string, unknown> | null {
  return v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : null;
}

export function mapAnnouncementObjectToDetailForm(inner: Record<string, unknown>): AnnouncementAdminDetailFormShape {
  const id = inner.id ?? inner.announcementId ?? inner.announcement_id;
  const mediaRaw = inner.media ?? inner.attachments ?? inner.fileList ?? inner.files;
  const media: AnnouncementAdminDetailMedia[] = Array.isArray(mediaRaw)
    ? (mediaRaw as unknown[])
        .map((item) => {
          const o = asRecord(item) ?? {};
          const url = String(o.url ?? o.fileUrl ?? o.file_url ?? '').trim();
          const type = String(o.type ?? o.mediaType ?? o.media_type ?? '').trim();
          const telegram = (o.telegram_file_id as string | undefined) ?? (o.telegramFileId as string | undefined);
          return url ? { type, url, ...(telegram ? { telegram_file_id: telegram } : {}) } : null;
        })
        .filter((x): x is AnnouncementAdminDetailMedia => x != null)
    : [];

  const str = (v: unknown) => (v != null && String(v).trim() !== '' ? String(v) : undefined);

  return {
    id: id != null && id !== '' ? (typeof id === 'number' || typeof id === 'string' ? id : str(id)) : undefined,
    title: str(inner.title),
    vendor: str(inner.vendor),
    purpose: str(inner.purpose ?? inner.purposeType ?? inner.purpose_type),
    period: str(inner.period),
    content: str(inner.content),
    media,
  };
}

export function parseAnnouncementAdminDetailResponse(res: unknown): AnnouncementAdminDetailFormShape {
  const root = asRecord(res);
  const inner = asRecord(root?.data) ?? root ?? {};
  return mapAnnouncementObjectToDetailForm(inner);
}

export async function fetchAnnouncementAdminDetail(id: string | number): Promise<AnnouncementAdminDetailFormShape> {
  const res = await Request.get(`${URL_ANNOUNCEMENT_ADMIN_LIST}/${String(id)}`);
  return parseAnnouncementAdminDetailResponse(res);
}

export type AnnouncementAdminListParams = {
  page: number;
  size: number;
};

export type AnnouncementDashboardListParams = {
  page: number;
  size: number;
};

function parseDashboardListResponse(res: unknown): { content: unknown[]; totalElements: number } {
  const r = res as { data?: { content?: unknown[]; totalElements?: number } } | undefined;
  const inner = r?.data;
  if (inner && typeof inner === 'object') {
    const content = inner.content;
    const list = Array.isArray(content) ? content : [];
    const totalElements = typeof inner.totalElements === 'number' ? inner.totalElements : list.length;
    return { content: list, totalElements };
  }
  return { content: [], totalElements: 0 };
}

export const useAnnouncementAdminList = (params: AnnouncementAdminListParams, enabled = true) => {
  return useQuery({
    queryKey: [QUERY_KEYS.ANNOUNCEMENT_ADMIN_LIST, params],
    queryFn: async () => {
      const res: any = await Request.get(URL_ANNOUNCEMENT_ADMIN_LIST, params);
      const content = res?.data?.content ?? res?.data?.data?.content ?? res?.data?.items ?? [];
      const total =
        res?.data?.totalElements ?? res?.data?.data?.totalElements ?? res?.data?.total ?? res?.data?.count ?? 0;
      return { content, total };
    },
    enabled,
  });
};

export async function fetchAnnouncementDashboardList(
): Promise<{ content: unknown[]; totalElements: number }> {
  const res = await Request.get(URL_ANNOUNCEMENT_DASHBOARD_LIST);
  return parseDashboardListResponse(res);
}

export const useAnnouncementDashboardList = (enabled = true) => {
  return useQuery({
    queryKey: [QUERY_KEYS.ANNOUNCEMENT_DASHBOARD_LIST],
    queryFn: () => fetchAnnouncementDashboardList(),
    enabled,
  });
};

export async function fetchAnnouncementDashboardDetail(id: string | number): Promise<Record<string, unknown>> {
  const res = (await Request.get(`${URL_ANNOUNCEMENT_DASHBOARD_LIST}/${String(id)}`)) as unknown as Record<
    string,
    unknown
  >;
  const inner = res?.data;
  if (inner && typeof inner === 'object' && !Array.isArray(inner)) {
    return inner as Record<string, unknown>;
  }
  return res;
}

export const AnnouncementServiceUrl = {
  adminList: URL_ANNOUNCEMENT_ADMIN_LIST,
  dashboardList: URL_ANNOUNCEMENT_DASHBOARD_LIST,
};
